// src/pages/B2B/event-reports/pdf/warm/charts/EngagementChart.tsx
//
// Layered chart: notif/peak label band, plot (active-users line + area), going-click bar
// strip, hour axis, legend. Direct port of the SVG composition from
// consumer_daily_report.py's _svg_engagement_chart.
//
// Returns a div containing raw SVG via dangerouslySetInnerHTML — the SVG is
// hand-built strings to match the daily report's output exactly. html2canvas
// captures SVG cleanly, so this approach gives pixel parity.

import React from 'react';
import type { ActiveUsersSample, BroadcastRow } from '../../../types';

interface Props {
  samples: ActiveUsersSample[];
  goingClickIsoTimes: string[];
  notifEvents: BroadcastRow[];
  timezone: string;
  windowStartUtcIso: string;
  windowMinutes: number;
  width?: number;
  height?: number;
  showGoingBars?: boolean;
  highlightTopPeaks?: number;  // pick top N by non-max suppression (>= 60min apart)
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function formatTime(tsMs: number, timeZone: string): string {
  return new Date(tsMs).toLocaleTimeString('en-US', { timeZone, hour: 'numeric', minute: '2-digit' });
}

function formatDay(tsMs: number, timeZone: string): string {
  return new Date(tsMs).toLocaleDateString('en-US', { timeZone, month: 'numeric', day: 'numeric' });
}

function tzAbbr(tsMs: number, timeZone: string): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(new Date(tsMs))
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

// Widest going-click bucket that still leaves the strip readable (~300 bars).
const GOING_BUCKETS_MIN = [5, 15, 30, 60, 120, 180, 360, 720, 1440];

export function EngagementChart(props: Props): React.ReactElement {
  const {
    samples,
    goingClickIsoTimes,
    notifEvents,
    timezone,
    windowStartUtcIso,
    windowMinutes,
    width = 760,
    height = 320,
    showGoingBars = true,
    highlightTopPeaks = 0,
  } = props;

  const padL = 44;
  const padR = 18;
  const startMs = new Date(windowStartUtcIso).getTime();
  const windowMs = windowMinutes * 60_000;

  if (samples.length === 0) {
    return (
      <div
        className="chart"
        dangerouslySetInnerHTML={{
          __html:
            `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">` +
            `<rect width="${width}" height="${height}" fill="#fff5f1" rx="14"/>` +
            `<text x="${width / 2}" y="${height / 2}" text-anchor="middle" fill="#7a6266" font-family="Montserrat, sans-serif" font-size="13">No samples available for this window.</text>` +
            `</svg>`,
        }}
      />
    );
  }

  // Layout
  const notifBandH = (notifEvents.length || highlightTopPeaks > 0) ? 60 : 14;
  const goingStripH = (showGoingBars && goingClickIsoTimes.length) ? 30 : 0;
  const hourBandH = 22;
  const legendH = 22;
  const plotTop = notifBandH + 8;
  const plotBottom = height - hourBandH - legendH - goingStripH - 4;
  const plotH = Math.max(80, plotBottom - plotTop);
  const plotW = width - padL - padR;
  const plotLeft = padL;
  const plotRight = width - padR;

  const maxCount = Math.max(1, ...samples.map((s) => s.count));
  const yTop = Math.max(maxCount + Math.max(2, Math.round(maxCount * 0.15)), 5);

  const xAt = (tsMs: number) =>
    plotLeft + Math.max(0, Math.min(1, (tsMs - startMs) / windowMs)) * plotW;
  const yAt = (count: number) => plotTop + plotH - (count / yTop) * plotH;

  const pts = samples.map((s) => [xAt(s.ts_ms), yAt(s.count)] as const);
  const linePath = 'M ' + pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L ');
  const areaPath =
    `M ${pts[0][0].toFixed(1)},${(plotTop + plotH).toFixed(1)} ` +
    pts.map(([x, y]) => `L ${x.toFixed(1)},${y.toFixed(1)}`).join(' ') +
    ` L ${pts[pts.length - 1][0].toFixed(1)},${(plotTop + plotH).toFixed(1)} Z`;

  // Going-click bars (5-min buckets, wider for long windows)
  const bucketMin =
    GOING_BUCKETS_MIN.find((b) => windowMinutes / b <= 300) ?? 1440;
  const bucketLabel = bucketMin < 60 ? `${bucketMin} min` : `${bucketMin / 60} hr`;
  let goingBarsSvg = '';
  if (showGoingBars && goingClickIsoTimes.length) {
    const stripTop = plotBottom + 4;
    const nBuckets = Math.max(1, Math.floor(windowMinutes / bucketMin));
    const buckets = new Array<number>(nBuckets).fill(0);
    for (const iso of goingClickIsoTimes) {
      const offsetMin = (new Date(iso).getTime() - startMs) / 60_000;
      const bi = Math.min(nBuckets - 1, Math.max(0, Math.floor(offsetMin / bucketMin)));
      buckets[bi] += 1;
    }
    const maxB = Math.max(1, ...buckets);
    const barW = plotW / nBuckets;
    const parts: string[] = [
      `<rect x="${plotLeft.toFixed(1)}" y="${stripTop.toFixed(1)}" width="${plotW.toFixed(1)}" height="${(goingStripH - 4).toFixed(1)}" fill="#f0fbf5" rx="3"/>`,
    ];
    buckets.forEach((b, i) => {
      if (!b) return;
      const h = (b / maxB) * (goingStripH - 6);
      parts.push(
        `<rect x="${(plotLeft + i * barW + 0.5).toFixed(1)}" y="${(stripTop + (goingStripH - 4) - h - 1).toFixed(1)}" width="${Math.max(1, barW - 1).toFixed(1)}" height="${h.toFixed(1)}" fill="#10b981" opacity="0.85" rx="1"/>`,
      );
    });
    parts.push(
      `<text x="${(plotLeft - 6).toFixed(1)}" y="${(stripTop + (goingStripH - 4) / 2 + 3).toFixed(1)}" text-anchor="end" font-size="9" fill="#5a6c64" font-family="Montserrat, sans-serif">going</text>`,
    );
    goingBarsSvg = parts.join('');
  }

  // Peaks: top N via non-max suppression (>= 60min apart)
  let peakSvg = '';
  const topPeaks: { tsMs: number; count: number }[] = [];
  if (highlightTopPeaks > 0) {
    const sorted = [...samples].sort((a, b) => b.count - a.count);
    for (const s of sorted) {
      if (topPeaks.every((p) => Math.abs(p.tsMs - s.ts_ms) >= 60 * 60_000)) {
        topPeaks.push({ tsMs: s.ts_ms, count: s.count });
      }
      if (topPeaks.length >= highlightTopPeaks) break;
    }
    const sortedByTime = topPeaks.slice().sort((a, b) => a.tsMs - b.tsMs);
    const minGap = 140;
    const rows: number[] = [];
    const assignments: number[] = [];
    for (const p of sortedByTime) {
      const x = xAt(p.tsMs);
      let placed = -1;
      for (let i = 0; i < rows.length; i++) {
        if (x - rows[i] >= minGap) { rows[i] = x; placed = i; break; }
      }
      if (placed < 0) { rows.push(x); placed = rows.length - 1; }
      assignments.push(placed);
    }
    const rowY = [16, 38];
    const parts: string[] = [];
    sortedByTime.forEach((p, i) => {
      const x = xAt(p.tsMs);
      const y = yAt(p.count);
      const labelY = rowY[Math.min(assignments[i], rowY.length - 1)];
      const anchor = (x + 8) > (plotRight - 110) ? 'end' : 'start';
      const labelX = anchor === 'end' ? x - 8 : x + 8;
      const topY = labelY + 22;
      parts.push(
        `<circle cx="${x.toFixed(1)}" cy="${topY.toFixed(1)}" r="9" fill="#c41e3a" opacity="0.18"/>`,
        `<circle cx="${x.toFixed(1)}" cy="${topY.toFixed(1)}" r="5" fill="#c41e3a" stroke="white" stroke-width="2"/>`,
        `<line x1="${x.toFixed(1)}" y1="${(topY + 7).toFixed(1)}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="#c41e3a" stroke-width="1.2" stroke-dasharray="3 3" opacity="0.85"/>`,
        `<text x="${labelX.toFixed(1)}" y="${labelY.toFixed(1)}" text-anchor="${anchor}" font-size="11" font-weight="800" fill="#c41e3a" font-family="Montserrat, sans-serif">${p.count} active users</text>`,
        `<text x="${labelX.toFixed(1)}" y="${(labelY + 13).toFixed(1)}" text-anchor="${anchor}" font-size="10.5" fill="#7a6266" font-family="Montserrat, sans-serif">at ${escapeXml(`${formatTime(p.tsMs, timezone)} ${tzAbbr(p.tsMs, timezone)}`)}</text>`,
      );
    });
    peakSvg = parts.join('');
  }

  // Notification arrows
  let notifSvg = '';
  if (notifEvents.length) {
    const prepped = notifEvents.map((ev) => {
      const tsMs = new Date(ev.sent_at_utc).getTime();
      const x = xAt(tsMs);
      const phx = formatTime(tsMs, timezone);
      let title = (ev.title || '').trim();
      if (title.length > 36) title = title.slice(0, 35) + '…';
      return { x, phx, title };
    }).sort((a, b) => a.x - b.x);
    const minGap = 110;
    const rows: number[] = [];
    const assignments: number[] = [];
    for (const p of prepped) {
      let placed = -1;
      for (let i = 0; i < rows.length; i++) {
        if (p.x - rows[i] >= minGap) { rows[i] = p.x; placed = i; break; }
      }
      if (placed < 0) { rows.push(p.x); placed = rows.length - 1; }
      assignments.push(placed);
    }
    const rowY = [16, 38];
    const parts: string[] = [];
    prepped.forEach((p, i) => {
      const baseY = rowY[Math.min(assignments[i], rowY.length - 1)];
      const anchor = (p.x + 4) > (plotRight - 100) ? 'end' : 'start';
      const labelX = anchor === 'end' ? p.x - 4 : p.x + 4;
      const lineYTop = baseY + 22;
      parts.push(
        `<line x1="${p.x.toFixed(1)}" y1="${(lineYTop + 6).toFixed(1)}" x2="${p.x.toFixed(1)}" y2="${plotBottom.toFixed(1)}" stroke="#c41e3a" stroke-width="1.4" stroke-dasharray="4 3" opacity="0.9"/>`,
        `<polygon points="${(p.x - 5).toFixed(1)},${(lineYTop - 4).toFixed(1)} ${(p.x + 5).toFixed(1)},${(lineYTop - 4).toFixed(1)} ${p.x.toFixed(1)},${(lineYTop + 6).toFixed(1)}" fill="#c41e3a"/>`,
        `<text x="${labelX.toFixed(1)}" y="${baseY.toFixed(1)}" text-anchor="${anchor}" font-size="10.5" font-weight="700" fill="#c41e3a" font-family="Montserrat, sans-serif">${escapeXml(p.phx)}</text>`,
        `<text x="${labelX.toFixed(1)}" y="${(baseY + 12).toFixed(1)}" text-anchor="${anchor}" font-size="10" fill="#7a6266" font-family="Montserrat, sans-serif">${escapeXml(p.title)}</text>`,
      );
    });
    notifSvg = parts.join('');
  }

  // Hour gridlines + labels. Past a day and a half the axis switches to dates,
  // at most ~12 ticks, so a month-long window stays legible.
  const hours = Math.max(1, Math.floor(windowMinutes / 60));
  const multiDay = hours > 36;
  const stepH = multiDay ? Math.ceil(hours / 12 / 24) * 24 : Math.ceil(hours / 12);
  const grid: string[] = [];
  const labels: string[] = [];
  const hourLabelY = plotBottom + goingStripH + 16;
  for (let h = 0; h <= hours; h += stepH) {
    const x = plotLeft + (h / hours) * plotW;
    grid.push(
      `<line x1="${x.toFixed(1)}" y1="${plotTop.toFixed(1)}" x2="${x.toFixed(1)}" y2="${plotBottom.toFixed(1)}" stroke="#f5e1e0" stroke-width="1"/>`,
    );
    const tickMs = startMs + h * 60 * 60_000;
    const lbl = multiDay
      ? formatDay(tickMs, timezone)
      : formatTime(tickMs, timezone).replace(/^(\d+):00\s/, '$1 ');
    labels.push(
      `<text x="${x.toFixed(1)}" y="${hourLabelY}" text-anchor="middle" font-size="11" fill="#7a6266" font-family="Montserrat, sans-serif">${escapeXml(lbl)}</text>`,
    );
  }

  // Y axis ticks
  const yAxis: string[] = [];
  for (const frac of [0, 0.25, 0.5, 0.75, 1.0]) {
    const v = Math.round(yTop * frac);
    const y = plotTop + plotH - frac * plotH;
    yAxis.push(
      `<text x="${(plotLeft - 8).toFixed(1)}" y="${(y + 3.5).toFixed(1)}" text-anchor="end" font-size="10" fill="#9c8b8e" font-family="Montserrat, sans-serif">${v}</text>`,
      `<line x1="${plotLeft}" y1="${y.toFixed(1)}" x2="${plotRight}" y2="${y.toFixed(1)}" stroke="#f7e7e6" stroke-width="1"/>`,
    );
  }

  const baseAxis = `<line x1="${plotLeft}" y1="${plotBottom.toFixed(1)}" x2="${plotRight}" y2="${plotBottom.toFixed(1)}" stroke="#eadfe0" stroke-width="1.5"/>`;

  // Legend
  const legendY = height - 8;
  const legendParts: string[] = ['<g font-family="Montserrat, sans-serif" font-size="10.5" fill="#5a4f52">'];
  let cx = plotLeft;
  legendParts.push(`<rect x="${cx}" y="${legendY - 7}" width="14" height="3" rx="1.5" fill="#c41e3a"/>`);
  legendParts.push(`<text x="${cx + 20}" y="${legendY - 4}">Live active users</text>`);
  cx += 155;
  if (showGoingBars && goingClickIsoTimes.length) {
    legendParts.push(`<rect x="${cx}" y="${legendY - 9}" width="10" height="7" rx="1" fill="#10b981" opacity="0.85"/>`);
    legendParts.push(`<text x="${cx + 16}" y="${legendY - 4}">Going clicks (per ${bucketLabel})</text>`);
    cx += 175;
  }
  if (notifEvents.length) {
    legendParts.push(`<line x1="${cx}" y1="${legendY - 6}" x2="${cx + 16}" y2="${legendY - 6}" stroke="#c41e3a" stroke-width="1.6" stroke-dasharray="4 3"/>`);
    legendParts.push(`<text x="${cx + 22}" y="${legendY - 4}">Notification sent</text>`);
  }
  if (topPeaks.length) {
    legendParts.push(`<circle cx="${cx + 5}" cy="${legendY - 6}" r="4" fill="#c41e3a"/>`);
    legendParts.push(`<text x="${cx + 14}" y="${legendY - 4}">Peak</text>`);
  }
  legendParts.push('</g>');

  const gradId = `lineGrad${Math.floor(startMs / 1000)}`;
  const svg =
    `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Engagement timeline">` +
    `<defs><linearGradient id="${gradId}" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#c41e3a" stop-opacity="0.42"/><stop offset="100%" stop-color="#c41e3a" stop-opacity="0.03"/></linearGradient></defs>` +
    `<rect width="${width}" height="${height}" fill="white" rx="14"/>` +
    yAxis.join('') + grid.join('') + baseAxis +
    `<path d="${areaPath}" fill="url(#${gradId})"/>` +
    `<path d="${linePath}" fill="none" stroke="#c41e3a" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>` +
    goingBarsSvg + peakSvg + notifSvg + labels.join('') + legendParts.join('') +
    `</svg>`;

  return <div className="chart" dangerouslySetInnerHTML={{ __html: svg }} />;
}
